import { ErrorResponse } from 'types/form';
import { ApiResources, createErrorResponse, getRequestHeaders, getUrl, Methods, processResponse } from './utils';

interface IProgressHistoryRequest {
  contactId: string;
  leadId: string;
  nextScreen: string;
  completedScreen: string;
}

export interface IProgressHistoryResponse {
  nextScreen: string;
  lastCompletedScreen: string;
}

const RETRY_ATTEMPTS = 2;
const RETRY_DELAY_MS = 1000;
const RETRY_STATUS_CODES = [404, 500, 401];
const RETRY_STATUS_UNAUTH_CODE = [401];

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const shouldRetry = (response: Response, retryStatusCodes: number[]): boolean => {
  return retryStatusCodes.includes(response.status);
};

const fetchWithRetry = async (
  endpoint: string, 
  request: RequestInit,
  attempts: number = RETRY_ATTEMPTS,
  retryStatusCodes: number[] = RETRY_STATUS_CODES
): Promise<Response> => {
  try {
    const response = await fetch(endpoint, request);

    if (!response.ok && shouldRetry(response, retryStatusCodes) && attempts > 0) {
      await delay(RETRY_DELAY_MS);
      return fetchWithRetry(endpoint, request, attempts - 1, retryStatusCodes);
    }

    return response;
  } catch (error) {
    if (attempts > 0) {
      await delay(RETRY_DELAY_MS);
      return fetchWithRetry(endpoint, request, attempts - 1, retryStatusCodes);
    }
    throw error;
  }
};

export const updateProgressHistory = async (
  progressHistoryRequest: IProgressHistoryRequest
): Promise<IProgressHistoryResponse | ErrorResponse> => {
  const endpoint = `${getUrl(ApiResources.progressHistory)}`;
  const request = {
    method: Methods.put,
    headers: await getRequestHeaders(),
    body: JSON.stringify(progressHistoryRequest),
  };

  try {
    const jsonResponse = await fetchWithRetry(endpoint, request);
    return processResponse(jsonResponse);
  } catch (error) {
    return createErrorResponse(error instanceof Error ? error.name : 400, error instanceof Error ? error.message : '');
  }
};

export const fetchProgressHistory = async (
  contactId: string,
  leadId: string
): Promise<IProgressHistoryResponse | ErrorResponse> => {
  const getProgressHistoryEndpoint = `${getUrl(ApiResources.getProgressHistory)}/${contactId}/lead/${leadId}`;

  try {
    const request = {
      method: Methods.get,
      headers: await getRequestHeaders(),
    };
    const progressHistoryResponse = await fetchWithRetry(
      getProgressHistoryEndpoint,
      request,
      RETRY_ATTEMPTS,
      RETRY_STATUS_UNAUTH_CODE
    );
    return processResponse(progressHistoryResponse);
  } catch (error) {
    return createErrorResponse(error instanceof Error ? error.name : 400, error instanceof Error ? error.message : '');
  }
};
